// Pre-inspections widget — open climbs (Ship 2; company scope added Ship 3).
//
// A climb only matters BEFORE the adjuster meeting happens, so the query is
// gated to the pre-meeting claims stages (the bare assigned+not-completed
// predicate matches dead/built deals — review-verified against live data:
// 11 rows for Sam, 9 of them closed or already building). Oldest-in-stage
// first; header shows the TRUE count, display capped with "+N more".
//
// scope="mine"    — the RS's own assigned climbs (Sam's board).
// scope="company" — every open climb, UNASSIGNED FIRST, because assigning them
//                   is the roof team manager's job (Robert's board).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

const CLIMB_STAGES = ["pre_inspection_ready", "adjuster_meeting_ready", "adjuster_meeting_scheduled"];
const SHOW = 5;

interface PreRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  pre_inspection_assigned_to?: string | null;
}

export default function PreinspWidget({
  memberId,
  scope = "mine",
}: {
  memberId: string;
  scope?: "mine" | "company";
}) {
  const [rows, setRows] = useState<PreRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let q = supabase
        .from("deals")
        .select("id,homeowner_name,property_street,pre_inspection_assigned_to")
        .eq("pre_inspection_completed", false)
        .in("stage", CLIMB_STAGES)
        .order("stage_entered_at", { ascending: true });
      if (scope === "mine") q = q.eq("pre_inspection_assigned_to", memberId);
      const { data, error } = await q;
      if (cancelled) return;
      // A failed read must not render as "All clear ✓".
      if (error) {
        setFailed(true);
        return;
      }
      const all = (data as PreRow[]) ?? [];
      // Company view: the ones nobody owns come first — that's the action.
      setRows(
        scope === "company"
          ? [...all].sort(
              (a, b) =>
                Number(!!a.pre_inspection_assigned_to) - Number(!!b.pre_inspection_assigned_to),
            )
          : all,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId, scope]);

  const unassigned =
    scope === "company" && rows ? rows.filter((r) => !r.pre_inspection_assigned_to).length : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🧗</span>
        <span className="font-bold text-sm text-bardin-ink">Pre-inspections</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : rows ? `${rows.length} open` : "…"}
        </span>
      </div>
      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load pre-inspections — try a refresh.</div>
      ) : !rows ? (
        <div className="h-9 rounded-lg bg-slate-100 animate-pulse mt-1" />
      ) : rows.length === 0 ? (
        <div className="text-xs text-emerald-700 py-2 font-semibold">All clear — no open climbs ✓</div>
      ) : (
        <>
          {unassigned > 0 && (
            <div className="text-[10.5px] text-amber-700 font-semibold mb-1">
              ⚠ {unassigned} not assigned to an RS yet
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {rows.slice(0, SHOW).map((r) => (
              <Link
                key={r.id}
                to={`/deal/${r.id}`}
                onClick={() => logCardClick("widget_preinsp_row")}
                className="flex items-center gap-2 py-1.5"
              >
                <span className="text-[13px] font-bold text-bardin-ink truncate">
                  {r.homeowner_name ?? "—"}
                </span>
                <span className="text-[11px] text-slate-400 truncate flex-1">{r.property_street ?? ""}</span>
                {scope === "company" && !r.pre_inspection_assigned_to && (
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-extrabold whitespace-nowrap">
                    unassigned
                  </span>
                )}
                <span className="text-slate-300">›</span>
              </Link>
            ))}
          </div>
          {rows.length > SHOW && (
            <div className="text-[10px] text-slate-400 font-semibold mt-1">+{rows.length - SHOW} more</div>
          )}
        </>
      )}
      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/dispatch?tab=preinspections"
          onClick={() => logCardClick("widget_preinsp_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Dispatch ›
        </Link>
      </div>
    </div>
  );
}
